import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import Button from './button.js';
import Copy from './copy.js';
import Header from './header.js';
import Icon from './icon.js';
import Text from './text.js';
import CDNIllustration from './cdn-illustration.js';
const DetailsDrawer = styled(Button).attrs(() => ({ variation: 'noOutline' })).withConfig({ displayName: "brc-DetailsDrawer", componentId: "brc-1ft6izc" }) `
  display: flex;
  justify-content: center;
  align-items: center;

  & > ${Copy} {
    padding-right: 8px;
  }

  & > ${Icon} {
    fill: ${({ theme }) => theme.neutral400};
  }
`;
const DisclaimerAsterisk = styled(Text).withConfig({ displayName: "brc-DisclaimerAsterisk", componentId: "brc-n0gojj" }) `
  font-size: 12px;
  position: absolute;
`;
const Flag = styled.div.withConfig({ displayName: "brc-Flag", componentId: "brc-1dw9f0l" }) `
  display: inline-block;
  background-color: ${({ theme }) => theme.azure};
  padding: 4px 8px;
  border-radius: 0 0 10px 10px;
  width: 128px;
  height: 32px;

  & > ${Copy} {
    color: ${({ theme }) => theme.white};
    text-align: center;
  }
`;
const PlanPrice = styled(Copy).withConfig({ displayName: "brc-PlanPrice", componentId: "brc-ghzkm2" }) `
  position: relative;
  & > ${DisclaimerAsterisk} {
    margin-left: 2px;
  }
`;
const StyledButton = styled(Button).attrs(() => ({ variation: 'outline', size: 'large' })).withConfig({ displayName: "brc-StyledButton", componentId: "brc-lh1ogj" }) ``;
const SelectPlanButton = styled.div.withConfig({ displayName: "brc-SelectPlanButton", componentId: "brc-v9qdyd" }) `
  width: min-content;
  margin: auto;
`;
const NoPaddingCard = styled.div.withConfig({ displayName: "brc-NoPaddingCard", componentId: "brc-11j99mb" }) `
  background-color: ${({ theme }) => theme.white};
  opacity: ${({ isSelected }) => (isSelected ? 0.5 : 1)};
  border-radius: 4px;
  width: 100%;
  box-shadow: 0 0 3px 0 ${({ theme }) => theme.neutral300}, 0 1px 2px 0 ${({ theme }) => theme.neutral300};
`;
const StripedRow = styled.div.withConfig({ displayName: "brc-StripedRow", componentId: "brc-1jxi314" }) `
  display: flex;
  &::before {
    content: '•';
    padding-right: 8px;
  }
`;
const StripedRowContent = styled(Copy).attrs(() => ({ size: 'sm' })).withConfig({ displayName: "brc-StripedRowContent", componentId: "brc-1ueak0d" }) `
  display: inline-block;
`;
const StripedContentWrapper = styled.div.withConfig({ displayName: "brc-StripedContentWrapper", componentId: "brc-1uaptyt" }) `
  & > ${StripedRow} {
    padding: 16px 24px;
  }
  & > ${StripedRow}:nth-child(odd) {
    background-color: ${({ theme }) => theme.neutral100};
  }
`;
const MobileStripedContentWrapper = styled(StripedContentWrapper).withConfig({ displayName: "brc-MobileStripedContentWrapper", componentId: "brc-1qui2ki" }) `
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    display: none;
  }
`;
const DesktopStripedContentWrapper = styled(StripedContentWrapper).withConfig({ displayName: "brc-DesktopStripedContentWrapper", componentId: "brc-1ls1vvm" }) `
  display: none;

  @media (${({ theme }) => theme.forLargerThanPhone}) {
    display: block;
  }
`;
const PlanHeadline = styled.div.withConfig({ displayName: "brc-PlanHeadline", componentId: "brc-k0l4ab" }) `
  width: 100%;
  padding-top: 16px;
`;
const Illustration = styled.img.withConfig({ displayName: "brc-Illustration", componentId: "brc-12fvj81" }) `
  width: auto;
  height: auto;
  max-height: 100px;
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    max-height: 200px;
  }
`;
const StyledCDNIllustration = styled(CDNIllustration).withConfig({ displayName: "brc-StyledCDNIllustration", componentId: "brc-bqr1ew" }) `
  width: auto;
  height: auto;
  max-height: 100px;
  @media (${({ theme }) => theme.forLargerThanPhone}) {
    max-height: 200px;
  }
`;
const PricingCardContent = styled.div.withConfig({ displayName: "brc-PricingCardContent", componentId: "brc-1w4tj2f" }) `
  display: grid;
  position: relative;
  padding: 24px;
  grid-gap: 16px;
  grid-template:
    'image plan plan'
    'image plan plan'
    'image description description'
    'button button button'
    'details details details' / auto auto auto;

  & > ${StyledCDNIllustration}, ${Illustration} {
    grid-area: image;
    margin: auto;
    padding-top: 24px;
  }

  & > ${PlanHeadline} {
    grid-area: plan;
    padding-bottom: ${({ shouldBeSelectable }) => (shouldBeSelectable ? '0px' : '24px')};
  }

  & > ${SelectPlanButton} {
    grid-area: button;
  }

  & > ${Copy} {
    grid-area: description;
  }

  & > ${DetailsDrawer} {
    grid-area: details;
    &:hover {
      background-color: ${({ theme }) => theme.white};
    }
  }

  & > ${Flag} {
    position: absolute;
    transform: rotateZ(-90deg);
    top: 44%;
    left: -48px;
  }

  @media (${({ theme }) => theme.forLargerThanPhone}) {
    grid-template-areas:
      'image image image'
      'plan plan plan'
      'button button button'
      'description description description';

    & > ${PlanHeadline}, & > ${Copy} {
      text-align: center;
    }

    & > ${Copy} {
      grid-area: description;
      padding: 0 36px;
    }

    & > ${DetailsDrawer} {
      display: none;
    }

    & > ${Flag} {
      transform: none;
      top: 0;
      left: calc(50% - 64px);
    }
  }
`;
export default ({ actionHref, actionTarget, altIllustration, altIllustrationText, description, disclaimerReference, features = [], illustration, isMostPopular, isSelected, planCode, planName, planPrice, shouldBeSelectable, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const renderFeatures = () => features.map((feature, i) => (_jsx(StripedRow, { children: _jsx(StripedRowContent, { children: _jsx(Text, { children: feature }) }) }, `${feature}${i}`)));
    return (_jsxs(NoPaddingCard, Object.assign({ isSelected: isSelected }, { children: [_jsxs(PricingCardContent, { children: [isMostPopular && (_jsx(Flag, Object.assign({ "data-testid": "pricing-card:is_most_popular" }, { children: _jsx(Copy, { children: "Most popular" }) }))), altIllustration ? (_jsx(Illustration, { src: altIllustration, alt: altIllustrationText })) : (!!illustration && _jsx(StyledCDNIllustration, { filename: illustration, "data-testid": "pricing-card:illustration" })), _jsxs(PlanHeadline, { children: [_jsx(Header, Object.assign({ size: "lg", "data-testid": "pricing-card:plan_name" }, { children: planName })), _jsxs(PlanPrice, Object.assign({ "data-testid": "pricing-card:plan_price" }, { children: [_jsx("strong", { children: planPrice }), _jsx(DisclaimerAsterisk, { children: '*'.repeat(disclaimerReference) })] }))] }), actionHref && shouldBeSelectable && (_jsx(SelectPlanButton, { children: isSelected ? (_jsx(StyledButton, Object.assign({ disabled: true, "data-testid": "pricing-card:is_selected" }, { children: "Current Plan" }))) : (_jsx(StyledButton, Object.assign({ href: actionHref, target: actionTarget, "data-testid": `pricing-card:select_this_plan_${planCode}` }, { children: "Select this plan" }))) })), _jsx(Copy, Object.assign({ bold: true }, { children: description })), !!features.length && (_jsxs(DetailsDrawer, Object.assign({ onClick: () => setIsOpen((isOpen) => !isOpen), "data-testid": "pricing-card:mobile_see_more_details" }, { children: [_jsx(Copy, Object.assign({ size: "sm", light: true }, { children: "See more details" })), _jsx(Icon, { name: isOpen ? 'actions/carrot-up' : 'actions/carrot-down' })] })))] }), isOpen && (_jsx(MobileStripedContentWrapper, Object.assign({ "data-testid": "pricing-card-mobile-features" }, { children: renderFeatures() }))), _jsx(DesktopStripedContentWrapper, Object.assign({ "data-testid": "pricing-card-desktop-features" }, { children: renderFeatures() }))] })));
};
//# sourceMappingURL=pricing-card.js.map